import {
  journeyStorage,
  type EpilotECPTokenPayload,
  type EpilotTokenPayload
} from '@epilot/journey-logic-commons'
import { jwtDecode } from 'jwt-decode'
import { useMemo } from 'react'

let isConfigured = false

async function configure(existingToken: string) {
  if (!isConfigured && existingToken) {
    // "https://cognito-idp.eu-central-1.amazonaws.com/eu-central-1_ASauSlhLe"
    // https://cognito-idp.<Region>.amazonaws.com/<your user pool ID>
    const claims = jwtDecode(existingToken)
    const iss = claims.iss
    const aud = claims.aud as string

    if (!iss || !aud) {
      return
    }
    // extracting the region and user pool id from the iss
    const userPoolId = iss.split('/')[3]

    // we must import it dynamically here as Amplify will crash the app if cookies are disabled in the browser settings
    const { Amplify } = await import('aws-amplify')

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: userPoolId,
          userPoolClientId: aud
        }
      }
    })

    isConfigured = true
  }
}

export function getLocalToken() {
  return journeyStorage.getItem('token')
}

export function usePortalCheck() {
  const token = getLocalToken()

  const isECPPortal = useMemo(() => {
    return isECP(token || '')
  }, [token])

  return { isECPPortal }
}

export function isECP(token: string) {
  try {
    if (!token) {
      return false
    }
    // token could be from 360 portal or ECP
    const decoded = jwtDecode<EpilotECPTokenPayload | EpilotTokenPayload>(token)

    // if this is ECP token then it should have origin field
    const origin = (decoded as EpilotECPTokenPayload)?.['custom:origin']

    return origin ? true : false
  } catch {
    return false
  }
}

async function getSession() {
  try {
    if (!isConfigured) {
      await configure(getLocalToken() || '')
    }

    // we must import it dynamically here as Amplify will crash the app if cookies are disabled in the browser settings
    const { fetchAuthSession } = await import('aws-amplify/auth')

    return await fetchAuthSession()
  } catch (err) {
    return null
  }
}

export function hasTokenExpired(token: string) {
  if (!token) {
    return true
  }

  const decoded = jwtDecode<EpilotECPTokenPayload | EpilotTokenPayload>(token)

  const exp = decoded?.exp
  const now = Date.now() / 1000

  return exp ? exp < now : true
}

export async function getPortalToken() {
  const localToken = getLocalToken()

  if (localToken) {
    // if token is not expired then return it
    if (!hasTokenExpired(localToken)) {
      return localToken
    }

    return await getTokenFromSession()
  }

  return localToken
}

async function getTokenFromSession(): Promise<string | null> {
  try {
    const session = await getSession()

    const idtoken = session?.tokens?.idToken?.toString()

    if (idtoken) {
      setTokenLocally(idtoken)

      return idtoken
    }

    return null
  } catch (err) {
    return null
  }
}

function setTokenLocally(token: string | null): void {
  if (token) {
    journeyStorage.setItem('token', token)
  }
}

export function getEmailFromToken(token: string) {
  try {
    if (!token) {
      return undefined
    }
    // token could be from 360 portal or ECP
    const decoded = jwtDecode<EpilotECPTokenPayload | EpilotTokenPayload>(token)

    // return the email from the token
    return decoded?.['email']
  } catch {
    return undefined
  }
}
